// Iter 90c — Lightweight collapsible section wrapper with localStorage
// persistence. Used to host Commissions / Partner Readiness on the
// Partners page and Resume Health on the Job Board page.
//
// Two visual modes:
//   • variant="framed" (default) — draws a bordered card around the header
//     AND the body. Use for content that has no Card of its own.
//   • variant="bare" — renders just a small toggle bar above the child.
//     Use when the child (e.g. CommissionsWidget) already renders its own
//     Card, so we avoid a double-border look.
import { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";

export function CollapsibleWidgetShell({
  storageKey, defaultOpen = true, title, subtitle, testid,
  variant = "framed", children,
}) {
  const [open, setOpen] = useState(() => {
    try {
      const v = localStorage.getItem(storageKey);
      if (v === "0") return false;
      if (v === "1") return true;
    } catch {
      /* sandboxed iframes may block localStorage */
    }
    return defaultOpen;
  });
  const toggle = () => {
    const next = !open;
    setOpen(next);
    try { localStorage.setItem(storageKey, next ? "1" : "0"); } catch { /* noop */ }
  };

  if (variant === "bare") {
    return (
      <section className="mb-3" data-testid={testid}>
        <button type="button" onClick={toggle}
                data-testid={testid ? `${testid}-toggle` : undefined}
                className="mb-1.5 flex w-full items-center justify-between gap-3 rounded-md px-1 py-1 text-left text-[11px] uppercase tracking-wider text-slate-500 hover:text-slate-700 transition">
          <span className="font-semibold truncate">{title}</span>
          <span className="inline-flex items-center gap-1">
            {open ? "Hide" : "Show"}
            {open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          </span>
        </button>
        {open && (
          <div data-testid={testid ? `${testid}-body` : undefined}>
            {children}
          </div>
        )}
      </section>
    );
  }

  return (
    <section className="mb-4 rounded-lg border border-slate-200 bg-white shadow-sm"
             data-testid={testid}>
      <button type="button" onClick={toggle}
              data-testid={testid ? `${testid}-toggle` : undefined}
              className="flex w-full items-center justify-between gap-3 rounded-t-lg px-4 py-2.5 text-left hover:bg-slate-50 transition">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-slate-800 truncate">{title}</div>
          {subtitle && <div className="text-[11px] text-slate-500 truncate">{subtitle}</div>}
        </div>
        <span className="flex items-center gap-1 text-xs text-slate-500">
          {open ? "Hide" : "Show"}
          {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </span>
      </button>
      {open && (
        <div className="border-t border-slate-100 p-4"
             data-testid={testid ? `${testid}-body` : undefined}>
          {children}
        </div>
      )}
    </section>
  );
}
